import { useState } from "react";
import { Button, TextField, Typography, Paper, Box, Fade } from "@mui/material";

import "./LoginPage.css";

import { AUTH_STRINGS, APP_STRINGS, COMMON_STRINGS } from "../../constants";
import { IMAGES } from "../../constants/assets";

import { sendOtp, verifyOtp } from "../../services/api";

// 🔥 Redux
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

const LoginPage = () => {
  const dispatch = useDispatch();

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);

  const [loading, setLoading] = useState(false);

  // ================= SEND OTP =================
  const handleSendOtp = async () => {
    if (!phone) {
      dispatch(
        showSnackbar({
          message: AUTH_STRINGS.PHONE_REQUIRED,
          type: "error",
        }),
      );
      return;
    }

    try {
      setLoading(true);

      const res = await sendOtp(phone);

      dispatch(
        showSnackbar({
          message: res.data.message || AUTH_STRINGS.SEND_OTP,
          type: "success",
        }),
      );

      setIsOtpSent(true);
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  // ================= VERIFY OTP =================
  const handleVerifyOtp = async () => {
    if (!otp) {
      dispatch(
        showSnackbar({
          message: AUTH_STRINGS.OTP_REQUIRED,
          type: "error",
        }),
      );
      return;
    }

    try {
      setLoading(true);

      const res = await verifyOtp(phone, otp);

      if (res.data.user?.role !== "admin") {
        dispatch(
          showSnackbar({
            message: "You are not authorized to access the admin panel.",
            type: "error",
          }),
        );

        return;
      }

      const token = res.data.token;

      localStorage.setItem("admin_token", token);

      dispatch(
        showSnackbar({
          message: res.data.message || AUTH_STRINGS.LOGIN_SUCCESS,
          type: "success",
        }),
      );

      // 🚀 Redirect
      setTimeout(() => {
        window.location.href = "/dashboard";
      }, 800);
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* LEFT SIDE */}
      <Box
        className="login-left"
        sx={{
          width: { xs: "0%", md: "50%" },
          display: { xs: "none", md: "flex" },
        }}
      >
        <Box className="login-left-content">
          <img src={IMAGES.LOGO_WHITE} alt="logo" className="login-left-logo" />

          <Typography variant="h4" className="login-left-title">
            {APP_STRINGS.APP_NAME}
          </Typography>

          <Typography className="login-left-subtitle">
            {AUTH_STRINGS.SUBTITLE}
          </Typography>
        </Box>
      </Box>

      {/* RIGHT SIDE */}
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Fade in timeout={600}>
          <Paper
            elevation={4}
            className="login-card"
            sx={{
              width: "90%",
              maxWidth: 400,
              p: { xs: 3, sm: 4 },
            }}
          >
            {/* LOGO */}
            <div className="login-logo">
              <img src={IMAGES.LOGO} alt="logo" />
            </div>

            {/* TITLE */}
            <Typography variant="h5" className="login-title">
              {AUTH_STRINGS.LOGIN_TITLE}
            </Typography>

            <Typography className="login-subtitle">
              {AUTH_STRINGS.SUBTITLE}
            </Typography>

            {/* PHONE */}
            <TextField
              fullWidth
              placeholder={AUTH_STRINGS.PHONE_PLACEHOLDER}
              value={phone}
              onChange={(e) =>
                setPhone(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))
              }
              inputProps={{ inputMode: "numeric", maxLength: 10 }}
              margin="normal"
            />

            {/* OTP */}
            {isOtpSent && (
              <TextField
                fullWidth
                placeholder={AUTH_STRINGS.OTP_PLACEHOLDER}
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
                }
                inputProps={{
                  inputMode: "numeric",
                  maxLength: 6,
                  autoComplete: "one-time-code",
                }}
                autoFocus
                margin="normal"
              />
            )}

            {/* BUTTON */}
            <Button
              fullWidth
              variant="contained"
              className="login-btn"
              disabled={loading || !phone || (isOtpSent && !otp)}
              onClick={isOtpSent ? handleVerifyOtp : handleSendOtp}
            >
              {loading
                ? "Please wait..."
                : isOtpSent
                  ? AUTH_STRINGS.VERIFY_OTP
                  : AUTH_STRINGS.SEND_OTP}
            </Button>
          </Paper>
        </Fade>
      </Box>
    </Box>
  );
};

export default LoginPage;
